// Vendor imports
const _ = require('lodash')
const React = require('react')
const classNames = require('classnames')
const { Mention } = require('react-mentions')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const EmojiMentionsInput = require('client-v2/common/components/emoji-mentions-input-lazy')
// Container imports
const ArticleProfileCommentsItem = require('./comments-item')

const MAX_COMMENT_LENGTH = 10000
const COMMENTS_PER_PAGE = 10
const INITIAL_COMMENTS_THRESHOLD = 5

// BEM Block entity definition
const bem = BemEntity('ArticleProfileComments')

function ArticleProfileComments(props) {
  return (
    tag('section', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag(CommentForm, props),
      tag(CommentsList, props)
    )
  )
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')

  ArticleProfileComments.propTypes = {
    // Flags
    isMoreAvailable: PropTypes.bool,
    isLoading: PropTypes.bool,
    // Values
    article: PropTypes.object.isRequired,
    className: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object),
    user: PropTypes.object.isRequired,
    // Callbacks
    onFormFocus: PropTypes.func,
    onFormSubmit: PropTypes.func,
    onItemDelete: PropTypes.func,
    onItemReport: PropTypes.func,
    onItemUserClick: PropTypes.func,
    onShowMore: PropTypes.func
  }
}

module.exports = ArticleProfileComments

// BEM Block entity Element components

function CommentForm(props) {
  const { article, user, isLoading } = props
  const [ commentText, setCommentText ] = React.useState('')

  if (!user.loggedIn) {
    return null
  }

  const onReset = event => {
    event.preventDefault()
    setCommentText('')
  }

  const onSubmit = event => {
    event.preventDefault()
    props.onFormSubmit({ article, user, commentText })
    setCommentText('')
  }

  const onInputChange = (event, newValue, newPlainTextValue) => {
    setCommentText(newPlainTextValue)
  }

  const placeholder = isLoading
  ? 'Fetching comments...'
  : 'Add a public comment...'

  const contacts = (user.contacts || []).map(contact => ({
    id: contact.complete,
    display: `${contact.complete} ${contact.names[0]}`,
    contact
  }))

  return (
    tag('form', {
      className: bem.element('form'),
      onReset,
      onSubmit
    },
      tag('div', {
        className: bem.element('form-field')
      },
        tag(UserAvatar, {
          className: bem.element('form-icon'),
          user: user,
          visible: true
        }),
        tag(EmojiMentionsInput, {
          disabled: isLoading,
          maxLength: MAX_COMMENT_LENGTH,
          placeholder,
          value: commentText,
          onChange: onInputChange,
          onFocus: props.onFormFocus
        },
          tag(Mention, {
            trigger: '@',
            data: contacts,
            displayTransform: id => `@${id} `,
            markup: '@__id__ ',
            renderSuggestion: renderContact
          })
        )
      ),
      tag('div', {
        className: bem.element('form-buttons', {
          submittable: commentText !== ''
        })
      },
        tag(CapsuleButton, {
          className: bem.element('form-reset'),
          type: 'reset'
        },
          'Cancel'
        ),
        tag(CapsuleButton, {
          className: bem.element('form-submit'),
          type: 'submit'
        },
          'Comment'
        )
      )
    )
  )
}

function CommentsList(props) {
  const { article, items, user } = props
  const [ visibleCount, setVisibleCount ] = React.useState(INITIAL_COMMENTS_THRESHOLD)

  if (!items.length || props.isLoading) {
    return null
  }

  const visibleItems = _.orderBy(items, 'created_at', 'desc').slice(0, visibleCount)

  const showMoreComments = () => {
    setVisibleCount(c => c + COMMENTS_PER_PAGE)
  }

  return tag([
    tag('ul', {
      className: bem.element('list')
    },
      visibleItems.map(comment => (
        tag(ArticleProfileCommentsItem, {
          key: `comments-item:${comment.id}`,
          className: bem.element('list-item'),
          article,
          comment,
          user,
          onDeleteClick: props.onItemDelete,
          onReportClick: props.onItemReport,
          onUserClick: props.onItemUserClick
        })
      ))
    ),
    tag(MoreButton, {
      isVisible: visibleCount < items.length,
      onClick: showMoreComments
    })
  ])
}

function MoreButton(props) {
  if (!props.isVisible) {
    return null
  }
  return (
    tag(CapsuleButton, {
      className: bem.element('more-button'),
      onClick: props.onClick
    },
      'Show more'
    )
  )
}

function renderContact(entry, search, highlightedDisplay, index, focused) {
  const { complete, photo, names } = entry.contact
  return (
    tag('div', {
      className: bem.element('contact', {
        active: focused
      })
    },
      tag(UserAvatar, {
        className: bem.element('contact-photo'),
        user: { 'photo_url': photo, username: complete }
      }),
      tag('span', {
        className: bem.element('contact-name')
      },
        names[0]
      ),
      tag('span', {
        className: bem.element('contact-username')
      },
        names[1]
      )
    )
  )
}
